<template>
  <view-edit-panel
    class="registration-data"
    v-model="assetInfo"
    :disable-save="invalidForm"
    :loading="loading"
    :key="assetInfo.identification"
    @changing="save"
    @cancel="clearValidationErrors"
  >
    <b-container
      slot-scope="data"
      fluid
      class="registration-data__fields"
    >
      <template v-if="!loading">
        <b-row class="registration-data__section-header px-3 py-1 mb-4 rounded-top">
          {{ $t('asset-information.asset-info.asset-data')}}
        </b-row>
        <b-row>
          <data-body
            :is-transformer="isTransformer"
            :asset-info="data.item"
            :saving="data.saving"
            :edit-mode="data.editing"
            :validation-errors.sync="validationErrors"
            @form-status-update="formStatusUpdate"
          />
        </b-row>
      </template>
      <b-row v-else class="registration-data__loader" align-h="center" align-v="center">
        <s-loader size="lg" />
      </b-row>
    </b-container>
  </view-edit-panel>
</template>

<style lang="sass" scoped>
.registration-data
  &__loader
    min-height: 500px
  &__fields label
    margin-bottom: 0
  &__section-header
    background-color: var(--color-background-dark)
    font-weight: bold
    font-size: .9em
</style>

<script>
import { AssetTypes, GenericError } from 'sigma-mappings';
import { assetsTypesIds } from '@/domain/data/installation/assets';
import { getKeyByValue } from '@/services/objectHelpers';
import registrationDataService from '@/domain/services/installation/assetInfo/registrationData';
import { onlyFirstCapitalized } from '@/services/stringHelper';
import loadingMixin from '@/services/loadingMixin';
import DataBody from '@/domain/components/installation/assetInformation/common/registrationData/Body';
import ViewEditPanel from '@/components/ViewEditPanel';
import TreeviewHeaderSubtitleMixin from '@/components/treeview/treeviewHeaderSubtitleMixin';
import globalEventBus from '@/services/globalEventBus';
import treeviewEvents from '@/domain/events/treeview';

export default {
  name: 'RegistrationData',
  props: {
    asset: {
      type: Object,
      default: () => ({}),
    },
    assetType: {
      type: Object,
      default: () => ({}),
    },
  },
  components: {
    DataBody,
    ViewEditPanel,
  },
  mixins: [
    loadingMixin,
    TreeviewHeaderSubtitleMixin,
  ],
  data: () => ({
    invalidForm: true,
    assetInfo: {},
    assetDataToUpdateTreeview: {},
    validationErrors: [],
  }),
  computed: {
    titleSetup() {
      return {
        preposition: this.$t('general.of'),
        assetType: onlyFirstCapitalized(this.assetType.name || ''),
      };
    },
    assetTypeEnumValue() {
      return getKeyByValue(assetsTypesIds, this.asset.assetTypeId);
    },
    isTransformer() {
      return this.assetTypeEnumValue === AssetTypes.TRANSFORMER;
    },
  },
  created() {
    this.getRegistrationData();
    this.updateHeaderTitle(
      this.$t('asset-information.asset-info.registration-data-title', this.titleSetup),
    );
  },
  methods: {
    clearValidationErrors() {
      this.validationErrors = [];
    },
    getRegistrationData() {
      this.startLoading();
      const { assetId } = this.$route.params;
      registrationDataService
        .getRegistrationAssetData(assetId)
        .then((response) => {
          this.assetInfo = response;
          this.setAssetDataToUpdateTreeview(response);
        })
        .finally(() => {
          this.endLoading();
        });
    },
    formStatusUpdate(value) {
      this.invalidForm = value;
    },
    setAssetDataToUpdateTreeview(assetData) {
      this.assetDataToUpdateTreeview = {
        identification: assetData.identification,
        serialNumber: assetData.serialNumber,
      };
    },
    modifiedAssetDataToUpdateTreeview(newValue) {
      const modifiedIdentification = this.assetDataToUpdateTreeview.identification
        !== newValue.identification;
      const modifiedSerialNumber = this.assetDataToUpdateTreeview.serialNumber
        !== newValue.serialNumber;
      return modifiedIdentification || modifiedSerialNumber;
    },
    save({ newValue, accept, reject }) {
      registrationDataService
        .updateRegistrationAssetData(newValue, this.asset.id)
        .then(() => {
          this.updateTreeviewData(newValue);
          this.clearValidationErrors();
          accept();
        })
        .catch((error) => {
          const rejectReason = new GenericError(error.response?.data);
          this.validationErrors = rejectReason.validationErrors || [];
          reject();
        });
    },
    updateTreeviewData(newValue) {
      if (this.modifiedAssetDataToUpdateTreeview(newValue)) {
        globalEventBus.emit(treeviewEvents.update);
        this.setAssetDataToUpdateTreeview(newValue);
      }
    },
  },
  watch: {
    asset() {
      this.getRegistrationData();
    },
    assetType(value) {
      if (value) {
        this.updateHeaderTitle(
          this.$t('asset-information.asset-info.registration-data-title', this.titleSetup),
        );
      }
    },
  },
};
</script>
